/**
 * Input/output contract for `bulkResolveUsers` — Phase C of the
 * resolve-and-propose architecture for the Users page import dialog.
 *
 * Mirrors the per-row shape of `createContact` (which the existing Users
 * dialog calls in a loop). The resolve callable does NOT write; it
 * returns per-row proposals so the import dialog can surface ADR-057
 * identity matches before any `createContact` calls fire. Replaces the
 * silent dedup-by-email behavior in `createContact`'s
 * `alreadyExists: true` branch with an explicit "this person already
 * exists in [Q3 Summit, Winter Forum]; proceed?" affordance.
 */

import { z } from 'zod';
import { roleSchema } from '../schemas/role';

export const bulkResolveUsersRowSchema = z
  .object({
    email: z.string().min(1),
    role: roleSchema.optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    honorificPrefix: z.string().nullable().optional(),
    honorificSuffix: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    organization: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    workCity: z.string().nullable().optional(),
    workState: z.string().nullable().optional(),
    bio: z.string().nullable().optional(),
    linkedIn: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    photoURL: z.string().nullable().optional(),
    dietaryRestrictions: z.array(z.string()).optional(),
    accessibilityNeeds: z.string().nullable().optional(),
    internalNotes: z.string().nullable().optional(),
    internalTags: z.array(z.string()).optional(),
  })
  .strict();

export type BulkResolveUsersRow = z.infer<typeof bulkResolveUsersRowSchema>;

export const bulkResolveUsersInputSchema = z
  .object({
    rows: z.array(bulkResolveUsersRowSchema).min(1).max(1000),
  })
  .strict();

export type BulkResolveUsersInput = z.infer<typeof bulkResolveUsersInputSchema>;

export type BulkResolveUsersRowStatus =
  /** Email matches no existing user or contact globally. createContact would create a new contact. */
  | 'new'
  /** Email matches an existing authenticated user globally. createContact would no-op (returns existing). */
  | 'matches_user'
  /** Email matches an existing contact globally. createContact would no-op (returns existing). */
  | 'matches_contact'
  | 'failed';

/**
 * One field's difference between the workbook row and the existing person/
 * profile record. Surfaced in the resolve dialog as a per-row diff card so
 * the organizer can see exactly what overwriting would change.
 */
export interface BulkResolveUsersFieldChange {
  /** Top-level key from the row (e.g. "bio", "title", "internalTags"). */
  field: string;
  /** The value currently stored — string or array (for tags / dietary). */
  existing: string | string[] | null;
  /** The value the workbook is proposing. */
  incoming: string | string[] | null;
}

export interface BulkResolveUsersProposal {
  source: 'existing_user' | 'existing_contact';
  userId?: string;
  contactId?: string;
  displayName: string;
  photoURL: string | null;
  /** Prior-event affiliations for the matched person. */
  priorEventNames: string[];
  /**
   * Per-field differences between the workbook row and the existing
   * person/profile record. Empty array when the workbook would not change
   * anything (organizer can safely skip the row).
   */
  proposedChanges: BulkResolveUsersFieldChange[];
}

export interface BulkResolveUsersRowResult {
  index: number;
  /** Original email as the caller passed it. */
  email: string;
  /** Lowercased + trimmed form, surfaced for dedupe in the dialog. */
  normalizedEmail: string;
  status: BulkResolveUsersRowStatus;
  /** Populated for every non-new / non-failed status. */
  proposal?: BulkResolveUsersProposal;
  /** Populated when status === 'failed'. */
  message?: string;
}

export interface BulkResolveUsersResponse {
  success: true;
  results: BulkResolveUsersRowResult[];
  summary: {
    new: number;
    matchesUser: number;
    matchesContact: number;
    failed: number;
  };
}
